/**
 * ActiveMelCard Component
 * Displays an active MEL with expiry countdown and clear action
 */

import React from 'react';
import { cn } from '@/utils/cn';
import type { ActiveMel } from '@/types/mel';
import { REPAIR_CATEGORIES } from '@/types/mel';
import melService from '@/services/melService';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

interface ActiveMelCardProps {
  mel: ActiveMel;
  onClear?: (mel: ActiveMel) => void;
  showAircraft?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

export const ActiveMelCard: React.FC<ActiveMelCardProps> = ({
  mel,
  onClear,
  showAircraft = true,
  variant = 'default',
  className,
}) => {
  const daysRemaining = melService.getDaysUntilExpiry(mel.expiresAt);
  const isExpired = melService.isExpired(mel.expiresAt);
  const isExpiringSoon = melService.isExpiringSoon(mel.expiresAt);
  const expiryStatus = melService.formatExpiryStatus(mel.expiresAt);

  const repairCat = REPAIR_CATEGORIES[mel.melItem.repairCategory] || {
    label: mel.melItem.repairCategory,
    days: null,
  };

  const getExpiryColor = () => {
    if (isExpired) return 'text-red-600 dark:text-red-400';
    if (isExpiringSoon) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getExpiryBgColor = () => {
    if (isExpired) return 'bg-red-100 dark:bg-red-900/30';
    if (isExpiringSoon) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-green-100 dark:bg-green-900/30';
  };

  const getExpiryIcon = () => {
    if (isExpired) return <XCircleIcon className="w-5 h-5" />;
    if (isExpiringSoon) return <ExclamationTriangleIcon className="w-5 h-5" />;
    return <CheckCircleIcon className="w-5 h-5" />;
  };

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg border border-border bg-card',
          className
        )}
      >
        {/* Expiry indicator */}
        <div className={cn('p-2 rounded-lg', getExpiryBgColor(), getExpiryColor())}>
          {getExpiryIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {showAircraft && (
              <span className="text-xs font-medium text-foreground/60">
                {mel.aircraft.tailNumber}
              </span>
            )}
            <span className="font-mono text-sm font-bold text-primary">
              {mel.melItem.itemNumber}
            </span>
          </div>
          <p className="text-sm text-foreground truncate">{mel.melItem.title}</p>
        </div>

        {/* Expiry */}
        <div className="flex-shrink-0 text-right">
          <p className={cn('text-sm font-medium', getExpiryColor())}>{expiryStatus}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border bg-card overflow-hidden',
        isExpired && 'border-red-300 dark:border-red-700',
        isExpiringSoon && !isExpired && 'border-yellow-300 dark:border-yellow-700',
        !isExpiringSoon && !isExpired && 'border-border',
        className
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Aircraft and Item Number */}
            <div className="flex items-center gap-2 flex-wrap">
              {showAircraft && (
                <>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-sm font-medium">
                    <PaperAirplaneIcon className="w-3 h-3" />
                    {mel.aircraft.tailNumber}
                  </span>
                  <span className="text-foreground/30">|</span>
                </>
              )}
              <span className="font-mono text-sm font-bold text-primary">
                {mel.melItem.itemNumber}
              </span>
              <span className="text-xs text-foreground/50">
                ATA {mel.melItem.ataChapter}
              </span>
            </div>

            {/* Title */}
            <h3 className="mt-1 text-lg font-semibold text-foreground">
              {mel.melItem.title}
            </h3>
          </div>

          {/* Expiry Badge */}
          <div
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              getExpiryBgColor(),
              getExpiryColor()
            )}
          >
            {getExpiryIcon()}
            <div className="text-right">
              <p className="text-sm font-medium">{expiryStatus}</p>
              {daysRemaining !== null && daysRemaining >= 0 && (
                <p className="text-xs opacity-80">
                  {mel.expiresAt && formatDate(mel.expiresAt).split(',')[0]}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Repair Category */}
        <div className="flex items-center gap-2 mt-3 text-sm text-foreground/60">
          <ClockIcon className="w-4 h-4" />
          <span>{repairCat.label}</span>
        </div>
      </div>

      {/* Description */}
      <div className="p-4 border-b border-border">
        <h4 className="text-sm font-medium text-foreground/60 mb-1">Issue Description</h4>
        <p className="text-sm text-foreground">{mel.description}</p>
      </div>

      {/* Details */}
      <div className="p-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <h4 className="text-foreground/60 mb-0.5">Issued By</h4>
          <p className="text-foreground">
            {mel.reportedBy.firstName} {mel.reportedBy.lastName}
          </p>
          <p className="text-xs text-foreground/50">{formatDate(mel.reportedAt)}</p>
        </div>
        <div>
          <h4 className="text-foreground/60 mb-0.5">Aircraft</h4>
          <p className="text-foreground">{mel.aircraft.tailNumber}</p>
          <p className="text-xs text-foreground/50">{mel.aircraft.aircraftType}</p>
        </div>
      </div>

      {/* Dispatch Remarks Preview */}
      {mel.melItem.remarks && (
        <div className="px-4 pb-4">
          <details className="text-sm">
            <summary className="cursor-pointer text-foreground/60 hover:text-foreground transition-colors">
              View Dispatch Remarks
            </summary>
            <p className="mt-2 p-3 rounded bg-muted text-foreground/80 whitespace-pre-wrap">
              {mel.melItem.remarks}
            </p>
          </details>
        </div>
      )}

      {/* Clear Action */}
      {onClear && !mel.clearedAt && (
        <div className="p-4 border-t border-border bg-muted/50">
          <button
            onClick={() => onClear(mel)}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg',
              'bg-green-600 hover:bg-green-700 text-white',
              'font-medium transition-colors'
            )}
          >
            <WrenchScrewdriverIcon className="w-4 h-4" />
            Clear MEL
          </button>
        </div>
      )}

      {/* Cleared Info */}
      {mel.clearedAt && mel.clearedBy && (
        <div className="p-4 border-t border-border bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="font-medium">Cleared</span>
          </div>
          <p className="text-sm text-green-600 dark:text-green-500 mt-1">
            By {mel.clearedBy.firstName} {mel.clearedBy.lastName} on{' '}
            {formatDate(mel.clearedAt)}
          </p>
          {mel.clearedNotes && (
            <p className="text-sm text-foreground/70 mt-2">{mel.clearedNotes}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveMelCard;
